import React, { forwardRef, useImperativeHandle, useRef, useState } from "react";
import { RCCGrid, RCCHookToast, RCCInput, RCCRefModalProps, RCCServicesApi, RCCSwitch } from "react-common-components";
import { useForm } from "react-hook-form";
import Modal from "src/components/Modal";
import { useTranslation } from "src/context/Translations/context";
import { getValidationErrors, standardErrorMessages } from "src/utils/errors";
import DeleteModal from "src/components/DeleteModal";
import Audit from "src/components/Audit";
import ModalSide from "src/components/Modal/styles";
import DateTimePicker from "src/components/DateTimePicker";
import LovCommercialPlans from "src/components/Lov/CommercialPlans";
import formatDate from "src/utils/formatDate";
import { IBusinessGroup, IComponentBusinessGroupModals, IRefBusinessGroupModals } from "./interface";
import { addAndChangeSchema, addPlanSchema, deleteSchema } from "./schemas";

const Modals: React.ForwardRefRenderFunction<IRefBusinessGroupModals, IComponentBusinessGroupModals> = ({
	paginationRef
}, ref) => {
	const { translate } = useTranslation();
	const {addToast} = RCCHookToast();
	const [selectedGroupCompany, setSelectedGroupCompany] = useState<IBusinessGroup>({} as IBusinessGroup);
    
	// Modal Delete
	const modalDelete = useRef<RCCRefModalProps>(null);
	const { handleSubmit : handleSubmitDelete, register : registerDelete, reset : resetDelete, setError : setErrorDelete, formState: { errors : errorsDelete }, clearErrors: clearErrorsDelete } = useForm();
	const submitDelete = async (data: any) => {
		try{	
			clearErrorsDelete();
			modalDelete.current?.setLoadingButton();

			await deleteSchema(translate).validate({...data, "efa001FantasiaConfirmation": selectedGroupCompany.efa001Fantasia},{
				abortEarly: false
			});

			await RCCServicesApi.deleteRequest({
				method: "BusinessGroup/Delete",
				urlParameters: {
					id: selectedGroupCompany.efa001Id
				}
			});

			addToast({
				color: "green",
				description: translate("Grupo empresa excluído com sucesso"),
				title: translate("Sucesso")
			});
			modalDelete.current?.resetLoadingButton();
			modalDelete.current?.closeModal();
			paginationRef.current?.reload();

		}catch(err){

			modalDelete.current?.resetLoadingButton();
			if(getValidationErrors(err, setErrorDelete))
				return;

			standardErrorMessages(err, translate("Erro ao excluir grupo empresa"), addToast);
		}
		
	};
	const actionButtonDelete = (row: IBusinessGroup) => {
		resetDelete(); 
		setSelectedGroupCompany(row); 
		modalDelete.current?.openModal();
	};
	
	// Modal Add/Change
	const [isAdd, setIsAdd] = useState(false);
	const modalAddOrChange = useRef<RCCRefModalProps>(null);
	const { 
		handleSubmit : handleSubmitAddOrChange, 
		control: controlAddOrChange,
		register : registerAddOrChange, 
		reset : resetAddOrChange, 
		setError : setErrorAddOrChange, 
		formState: { errors : errorsAddOrChange }, 
		clearErrors: clearErrorsAddOrChange 
	} = useForm();
	const onCallbackAddOrChange = useRef<Function | undefined>(undefined);
	const submitAddOrChange = async (data: any) => {
		try{
			clearErrorsAddOrChange();
			modalAddOrChange.current?.setLoadingButton();
			
			await addAndChangeSchema(translate).validate({...data, efa001Ativo: data.efa001Ativo ? data.efa001Ativo : ""}, {
				abortEarly: false
			});

			let businessGroupId = selectedGroupCompany.efa001Id;
			if(isAdd){
				await addPlanSchema(translate).validate({...data}, {
					abortEarly: false
				});
				const response = await RCCServicesApi.postRequest({
					method: "BusinessGroup/Insert",
					body: {
						...data,
						efa001Ativo: data.efa001Ativo ? "S" : "N",
						efa012Dtvalini: formatDate(data.efa012Dtvalini)
					}
				});
				businessGroupId = response.data.efa001Id;
			}else{
				await RCCServicesApi.putRequest({
					method: "BusinessGroup/Edit",
					body: {
						...data,
						efa001Id: selectedGroupCompany.efa001Id,
						efa001Ativo: data.efa001Ativo ? "S" : "N"
					}
				});
			}

			addToast({
				color: "green",
				description: isAdd ? translate("Grupo empresa adicionado com sucesso") : translate("Grupo empresa alterado com sucesso"),
				title: translate("Sucesso")
			});

			modalAddOrChange.current?.resetLoadingButton();
			modalAddOrChange.current?.closeModal();
			paginationRef.current?.reload();

			if(onCallbackAddOrChange.current)
				onCallbackAddOrChange.current(businessGroupId, data.efa001Razao, data.efa001Fantasia);

		}catch(err){

			modalAddOrChange.current?.resetLoadingButton();
			if(getValidationErrors(err, setErrorAddOrChange))
				return;

			standardErrorMessages(err, isAdd ? translate("Erro ao adicionar grupo empresa") : translate("Erro ao alterar grupo empresa"), addToast);
		}
	};
	const actionButtonAdd = (onCallback?: Function) => {
		resetAddOrChange(); 
		setSelectedGroupCompany({} as IBusinessGroup); 
		setIsAdd(true); 
		onCallbackAddOrChange.current = onCallback;
		modalAddOrChange.current?.openModal();
	};
	const actionButtonChange = (row: IBusinessGroup) => {
		resetAddOrChange(); 
		setSelectedGroupCompany(row); 
		setIsAdd(false); 
		onCallbackAddOrChange.current = undefined;
		modalAddOrChange.current?.openModal(); 
	};

	useImperativeHandle(ref, () => ({
		actionButtonAdd,
		actionButtonChange,
		actionButtonDelete
	}));
    
	return (
		<>
			{/* Modal Delete */}
			<DeleteModal
				ref={modalDelete}
				title= {translate("Exclusão do grupo de empresa")}
				description= {`${translate("Deseja realmente excluir o grupo")}: ${selectedGroupCompany.efa001Fantasia}?`}
				onSubmit = {handleSubmitDelete(submitDelete)} 
				height="250px"
			>
				<RCCGrid
					container
					item
					xs={10}
				>
					<RCCInput 
						register={registerDelete}
						name="efa001Fantasia"
						label={translate("Digite o nome do grupo para excluí-lo")}
						maxLength={200}
						error={errorsDelete.efa001Fantasia}
						required
					/>
				</RCCGrid>
			</DeleteModal>
			
			{/* Modal ADD/CHANGE */}
			<Modal
				ref={modalAddOrChange}
				header={{
					title: isAdd ? translate("Adicionar grupo de empresa") : translate("Alterar grupo de empresa"),
					icon: {
						name: "building"
					}
				}}
				onSubmit={handleSubmitAddOrChange(submitAddOrChange)}
				styleInline={ModalSide}
			>
				<RCCGrid
					container
					item
					direction="column"
					alignItems="center"
				>
					<RCCGrid
						container
						item
						xs={12}
						sm={10}
						alignItems="center"
						justify="flex-end"
					>
						<Audit data={selectedGroupCompany} type="pop"/>
					</RCCGrid>
					<RCCGrid
						container
						item
						xs={12}
						sm={10}
					>
						<RCCInput 
							register={registerAddOrChange}
							name="efa001Grupo"
							label={translate("Grupo empresarial")}
							maxLength={6}
							defaultValue={selectedGroupCompany.efa001Grupo}
							error={errorsAddOrChange.efa001Grupo}
							disabled={!isAdd}
							required
						/>
					</RCCGrid>
					<RCCGrid
						container
						item
						xs={12}
						sm={10}
					>
						<RCCInput 
							register={registerAddOrChange}
							name="efa001Razao"
							label={translate("Razão social")}
							maxLength={200}
							defaultValue={selectedGroupCompany.efa001Razao}
							error={errorsAddOrChange.efa001Razao}
							required
						/>
					</RCCGrid>
					<RCCGrid
						container
						item
						xs={12}
						sm={10}
					>
						<RCCInput 
							register={registerAddOrChange}
							name="efa001Fantasia"
							label={translate("Nome fantasia")}
							maxLength={200}
							defaultValue={selectedGroupCompany.efa001Fantasia}
							error={errorsAddOrChange.efa001Fantasia}
							required
						/>
					</RCCGrid>
					<RCCGrid
						container
						item
						xs={12}
						sm={10}
					>
						<RCCGrid
							container
							item
							xs={12}
							sm={6}
							md={4}
							lg={3}
						>
							<RCCSwitch 
								register={registerAddOrChange}
								name="efa001Ativo"
								label={translate("Ativo")}
								defaultChecked={selectedGroupCompany.efa001Ativo ? selectedGroupCompany.efa001Ativo.toString() === "S" : true}
							/>
						</RCCGrid>
					</RCCGrid>
					{isAdd && (
						<>
							<RCCGrid
								container
								item
								xs={12}
								sm={10}
							>
								<LovCommercialPlans 
									name="efd001Id"
									label={translate("Plano comercial")}
									control={controlAddOrChange}
									required
									error={errorsAddOrChange.efd001Id}
								/>
							</RCCGrid>
							<RCCGrid
								container
								item
								xs={12}
								sm={10}
							>
								<DateTimePicker
									name="efa012Dtvalini"
									type="datePicker"
									label={translate("Data inicial de validade")}
									control={controlAddOrChange}
									required
									error={errorsAddOrChange.efa012Dtvalini}
									defaultValue={new Date()}
								/>
							</RCCGrid>
						</>
					)}
				</RCCGrid>
			</Modal>
		</>
	);
};

export default forwardRef(Modals);
